import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ModalWrapper, SectionTitle } from 'modules/shared/components';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { getAllEmployees } from '../../employees/employees.service';
import {
  getLetterTemplates,
  issueLetter,
  LetterRow,
  LetterTemplate,
  previewLetter,
} from '../people.api';

interface EmployeeOption {
  id: number;
  name: string;
  employeeId: string;
  department: string;
}

/** `new_designation` → `New designation`, so a template can add a prompt without a UI change. */
const promptLabel = (key: string) =>
  key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Issues a letter: choose the employee and the template, answer whatever that template asks
 * for, then read the rendered text before it is committed.
 *
 * The preview is deliberately editable. Templates cover the sentence a letter usually needs;
 * the one case in twenty that needs a different sentence must not force HR out to a word
 * processor, so whatever is on screen at the moment of issue is what gets stored.
 */
export default function IssueLetterModal({
  open,
  employeeId,
  onClose,
  onIssued,
}: {
  open: boolean;
  /** Pre-selects an employee when opened from their record. */
  employeeId?: number;
  onClose: () => void;
  onIssued: (letter: LetterRow) => void | Promise<void>;
}) {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [loading, setLoading] = useState(false);

  const [employee, setEmployee] = useState<number | ''>('');
  const [letterType, setLetterType] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [issuedOn, setIssuedOn] = useState(today());
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const template = useMemo(
    () => templates.find((t) => t.code === letterType) ?? null,
    [templates, letterType]
  );

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([getAllEmployees(), getLetterTemplates()])
      .then(([staff, list]) => {
        // Letters go to people still on the books, and to leavers who need a relieving
        // letter or an experience certificate — so both lists are offered.
        setEmployees([...(staff.active ?? []), ...(staff.inactive ?? [])] as EmployeeOption[]);
        setTemplates(list);
      })
      .catch((err) =>
        setError(
          logError({ error: err, message: 'Failed to load letter templates', code: 'LETTER_SETUP_FAILED' })
        )
      )
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setEmployee(employeeId ?? '');
    setLetterType('');
    setValues({});
    setBody('');
    setSubject('');
    setIssuedOn(today());
    setError(null);
  }, [open, employeeId]);

  const handlePreview = async () => {
    if (!employee || !letterType) {
      setError('Choose an employee and a letter type first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await previewLetter({
        employee_id: employee,
        letter_type: letterType,
        issued_on: issuedOn,
        ...values,
      });
      setBody(result.body);
      setSubject(result.subject);
    } catch (err) {
      setError(
        logError({ error: err, message: 'Failed to render the letter', code: 'LETTER_PREVIEW_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleIssue = async () => {
    if (!employee || !letterType) {
      setError('Choose an employee and a letter type first.');
      return;
    }
    if (!body.trim()) {
      setError('Generate the letter before issuing it.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const letter = await issueLetter({
        employee_id: employee,
        letter_type: letterType,
        issued_on: issuedOn,
        subject,
        body,
        ...values,
      });
      toast.success(`Issued ${letter.reference_no}`);
      await onIssued(letter);
      onClose();
    } catch (err) {
      setError(
        logError({ error: err, message: 'Failed to issue the letter', code: 'LETTER_ISSUE_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalWrapper open={open} onClose={onClose} maxWidth='md'>
      <SectionTitle title='Issue a letter' />

      {error && <Alert severity='error'>{error}</Alert>}

      <Stack gap={2} sx={{ overflowY: 'auto' }}>
        <Stack direction='row' gap={2} flexWrap='wrap'>
          <TextField
            select
            size='small'
            label='Employee'
            value={employee}
            onChange={(e) => setEmployee(Number(e.target.value))}
            sx={{ minWidth: 260, flex: 1 }}
            disabled={loading || Boolean(employeeId)}
          >
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.name} — {emp.employeeId}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size='small'
            label='Letter type'
            value={letterType}
            onChange={(e) => {
              setLetterType(e.target.value);
              setValues({});
              setBody('');
            }}
            sx={{ minWidth: 260, flex: 1 }}
            disabled={loading}
          >
            {templates.map((t) => (
              <MenuItem key={t.code} value={t.code}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size='small'
            type='date'
            label='Issued on'
            value={issuedOn}
            onChange={(e) => setIssuedOn(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        {(template?.prompts?.length ?? 0) > 0 && (
          <Stack gap={2}>
            <Typography variant='body2' color='text.secondary'>
              This letter needs a few details the employee record does not hold:
            </Typography>
            <Stack direction='row' gap={2} flexWrap='wrap'>
              {template!.prompts.map((key) => (
                <TextField
                  key={key}
                  size='small'
                  label={promptLabel(key)}
                  value={values[key] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  sx={{ minWidth: 240, flex: 1 }}
                />
              ))}
            </Stack>
          </Stack>
        )}

        <Stack direction='row' gap={2}>
          <Button variant='outlined' onClick={handlePreview} disabled={busy || loading}>
            {body ? 'Regenerate' : 'Generate letter'}
          </Button>
        </Stack>

        {body && (
          <>
            <TextField
              size='small'
              label='Subject'
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
            />
            <TextField
              label='Letter'
              value={body}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={12}
              fullWidth
              helperText='Edit freely — the text as it stands here is what gets issued and stored.'
            />
          </>
        )}
      </Stack>

      <Stack direction='row' gap={2} justifyContent='flex-end'>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleIssue} disabled={busy || !body}>
          {busy ? 'Issuing…' : 'Issue letter'}
        </Button>
      </Stack>
    </ModalWrapper>
  );
}
